import axios from "axios";
import { GAME_ID } from "constants/constants";

/**
 * Remote config for the GameCityLoading billboard image.
 *
 * The platform admin's "Loading Screens" page curates scheduled billboards;
 * GET /api/public/games/loading-config?game=<code> returns the active one (or
 * null). This module caches the result in localStorage (stale-while-revalidate:
 * the cached URL is always usable synchronously on first render, and a refresh
 * runs in the background once the TTL lapses). Every failure path is silent —
 * when no URL is known the loading scene simply shows no billboard.
 *
 * Portable: copy alongside GameCityLoading when porting to another game; only
 * the GAME_ID import is game-specific.
 */

const STORAGE_KEY = "gc_loading_billboard";
const DEFAULT_TTL_MS = 5 * 60 * 1000;

// REACT_APP_API_URL has a trailing slash in some envs but not others.
const apiBase = () => (process.env.REACT_APP_API_URL || "").replace(/\/?$/, "/");

const readCache = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

/**
 * Synchronous read of the last known billboard URL (may be stale; null when
 * unknown or when the platform reported no active billboard).
 */
export const getCachedBillboardUrl = () => readCache()?.url || null;

let inFlight = null;

/**
 * Fetch the current loading config if the cache is missing or expired.
 * Resolves to the active billboard URL (or null). Never rejects.
 */
export const refreshLoadingConfig = () => {
    const cached = readCache();
    if (cached && Date.now() - cached.fetchedAt < (cached.ttlMs || DEFAULT_TTL_MS)) {
        return Promise.resolve(cached.url || null);
    }
    if (inFlight) return inFlight;

    inFlight = axios
        .get(`${apiBase()}api/public/games/loading-config`, { params: { game: GAME_ID }, timeout: 5000 })
        .then((res) => {
            const url = res.data?.billboardUrl || null;
            const ttlMs = (Number(res.data?.cacheTtlSeconds) || 300) * 1000;
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify({ url, fetchedAt: Date.now(), ttlMs }));
            } catch { /* storage blocked — still return the fresh value */ }
            return url;
        })
        .catch(() => cached?.url || null)
        .finally(() => { inFlight = null; });

    return inFlight;
};
